<template>
  <div class="settings-section">
    <h3 class="section-title">{{ $t('settings.business.title') }}</h3>
    <p class="section-description">{{ $t('settings.business.description') }}</p>

    <div class="settings-form">
      <div class="form-field">
        <IntercomInput
          v-model="formData.name"
          :label="$t('settings.business.businessName')"
          required
        />
      </div>
      <div class="form-field">
        <IntercomInput
          v-model="formData.address"
          :label="$t('settings.business.address')"
          type="textarea"
          :rows="3"
        />
      </div>
      <div class="form-row">
        <div class="form-field">
          <IntercomPhoneInput
            v-model="formData.phone"
            :label="$t('settings.business.phone')"
            :error="phoneError"
            @update:valid="(v) => (phoneValid = v)"
          />
        </div>
        <div class="form-field">
          <IntercomEmailInput
            v-model="formData.email"
            :label="$t('settings.business.email')"
            :error="emailError"
          />
        </div>
      </div>

      <!-- Tax Settings -->
      <div class="subsection">
        <h4 class="subsection-title">{{ $t('settings.business.taxSettings') }}</h4>

        <div class="form-field">
          <label class="form-label">{{ $t('settings.business.taxRegime') }}</label>
          <select v-model="formData.tax_regime" class="form-select">
            <option value="gst">{{ $t('settings.business.gstRegistered') }}</option>
            <option value="non_gst">{{ $t('settings.business.nonGst') }}</option>
          </select>
          <p class="form-help">{{ $t('settings.business.taxRegimeHelp') }}</p>
        </div>

        <template v-if="formData.tax_regime === 'gst'">
          <div class="form-row">
            <div class="form-field">
              <IntercomInput
                v-model="formData.gstin"
                :label="$t('settings.business.gstin')"
                maxlength="15"
              />
            </div>
            <div class="form-field">
              <IntercomInput
                v-model="formData.state_code"
                :label="$t('settings.business.stateCode')"
                maxlength="2"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Save Footer -->
    <div class="save-footer" v-if="isDirty || saveStatus">
      <button
        v-if="isDirty"
        class="btn-discard"
        @click="handleDiscard"
      >
        {{ $t('common.cancel') }}
      </button>
      <IntercomButton
        variant="primary"
        :class="{ saved: saveStatus === 'saved' }"
        :disabled="!isDirty && saveStatus !== 'saved'"
        @click="handleSave"
      >
        {{ isSaving ? $t('settings.saving') : saveStatus === 'saved' ? $t('settings.saved') + ' ✓' : $t('settings.saveChanges') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGym } from '../../composables/useGym.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { IntercomInput, IntercomPhoneInput, IntercomEmailInput, IntercomButton } from '@shared/components/intercom'

const { t } = useI18n()
const { getBusiness, updateBusiness } = useGym()
const { fetchBusiness: refreshBusinessName } = useBusiness()

const originalData = ref({})
const isSaving = ref(false)
const saveStatus = ref(null)
const emailError = ref('')
const phoneError = ref('')
const phoneValid = ref(true)

// Email validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formData = reactive({
  name: '',
  address: '',
  phone: '',
  email: '',
  tax_regime: 'non_gst',
  gstin: '',
  state_code: '',
})

// Track dirty state
const isDirty = computed(() => {
  return JSON.stringify(formData) !== JSON.stringify(originalData.value)
})

// Clear email error when user types
watch(() => formData.email, () => {
  emailError.value = ''
})

// Clear phone error when user types
watch(() => formData.phone, () => {
  phoneError.value = ''
})

const handleSave = async () => {
  if (!isDirty.value) return

  // Validate name
  if (!formData.name?.trim()) {
    return
  }

  // Validate email if provided
  if (formData.email && !emailRegex.test(formData.email)) {
    emailError.value = t('validation.invalidEmail')
    return
  }
  emailError.value = ''

  // Validate phone if provided
  if (formData.phone && !phoneValid.value) {
    phoneError.value = t('validation.invalidPhone')
    return
  }
  phoneError.value = ''

  isSaving.value = true
  try {
    await updateBusiness(formData)
    // Update original data to match current
    originalData.value = { ...formData }
    // Refresh the shared business state (updates topbar)
    await refreshBusinessName(true)
    saveStatus.value = 'saved'
    setTimeout(() => {
      saveStatus.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to save business settings:', error)
  } finally {
    isSaving.value = false
  }
}

const handleDiscard = () => {
  Object.assign(formData, originalData.value)
  emailError.value = ''
}

onMounted(async () => {
  try {
    const response = await getBusiness()
    const business = response?.business || response?.data || response
    if (business) {
      formData.name = business.name || ''
      formData.address = business.address || ''
      formData.phone = business.phone || ''
      formData.email = business.email || ''
      formData.tax_regime = business.tax_regime || 'non_gst'
      formData.gstin = business.gstin || ''
      formData.state_code = business.state_code || ''
      // Store original data for dirty checking
      originalData.value = { ...formData }
    }
  } catch (error) {
    console.error('Failed to fetch business:', error)
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-section {
  position: relative;
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-2) 0;
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-gray-200);
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.subsection {
  margin-top: var(--space-6);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-gray-200);
}

.subsection-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
  margin-bottom: var(--space-2);
}

.form-select {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
  cursor: pointer;
}

.form-select:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.form-help {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: var(--space-2) 0 0 0;
}

/* Save Footer */
.save-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.btn-discard {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s;
}

.btn-discard:hover {
  color: var(--color-charcoal);
}

.saved {
  background: #15803d !important;
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
